import {
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  InputGroup,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Switch,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import {
  Button,
  Footer,
  Heading,
  Input,
  ReactSelect,
  Select,
} from '~/components';
import {
  getGroupsAsSelect,
  useAccessTypes,
  useAuth,
  useExternalRoles,
  useInternalRoles,
  useQueryParams,
  useRoleCan,
} from '~/hooks';
import { getCompaniesByGroupAsSelect, getCompaniesByUser } from '~/services';
import { RoleEnum, SelectItem, User } from '~/types';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { User as AuthUser } from '../../../../contexts/AuthContext/types';
import { SalesSegmentForm } from '~/pages/Portal/Admin/Users/SalesSegmentForm';
import { useSalesSegmentAsSelect } from '~/hooks/useSalesSegment';
import { validateEmail } from '~/utils/emailRegex';
import { RolesEnum } from '~/utils/roles';

type FormData = {
  name: string;
  email: string;
  login: string;
  status: boolean;
  accessType: string;
  id_funcao: number;
  type: string;
  blocked: boolean;
  fontSize: string;
  multifator: boolean;
  responsavel: boolean;
  telefone_comercial: string;
  codigo_sap: string;
};

export const UserForm: React.VFC = () => {
  const toast = useToast();
  const userIsMasterClient = useRoleCan({ roles: ['cliente master'] });

  const { user: auth, updateUser } = useAuth();
  const history = useHistory();
  const queryParams = useQueryParams();
  const userId = queryParams.get('userId');
  const [type, setType] = useState(
    () =>
      queryParams.get('type') || (userIsMasterClient ? 'Cliente' : 'Interno'),
  );
  const isYourself = auth.id === Number(userId) && type === 'Interno';
  const [groups, setGroups] = useState<SelectItem[]>([]);
  const [branches, setBranches] = useState<SelectItem[]>([]);
  const [headOffices, setHeadOffices] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<SelectItem[]>([]);
  const [selectedHeadOffices, setSelectedHeadOffices] = useState<SelectItem[]>(
    [],
  );
  const [selectedBranches, setSelectedBranches] = useState<SelectItem[]>([]);
  const [accessType, setAccessType] = useState<string | undefined>(undefined);
  const [id_funcao, setRole] = useState<number | undefined>(undefined);
  const isEdition = !!userId && !!type;
  const [user, setUser] = useState<User | undefined>(undefined);
  const [isFetching, setFetching] = useState(isEdition);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
    getValues,
  } = useForm();
  const { errors } = formState;
  const [status, setStatus] = useState(true);
  const [bloqueado, setBloqueado] = useState(true);
  const [responsavel, setResponsavel] = useState(true);
  const [comissionado, setComissionado] = useState(false);
  const [visualizarComissoes, setVisualizarComissoes] = useState(false);
  const [clienteSucesso, setClienteSucesso] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<SelectItem[]>([]);
  const [segmentList, setSegmentList] = useState<SelectItem[]>([]);

  const [telefone, setTelefone] = useState('');
  const [noAccess, setNoAccess] = useState(false);

  const {
    data: accessTypes,
    isLoading: accessTypesLoading,
    refetch: refetchAccessTypes,
  } = useAccessTypes(type);

  const {
    data: internalRoles,
    isLoading: internalRoleLoading,
    refetch: internalRefetchRole,
  } = useInternalRoles();

  const {
    data: externalRoles,
    isLoading: externalRoleLoading,
    refetch: externalRefetchRole,
  } = useExternalRoles();

  const {
    data: segments,
    isLoading: isLoadingSegments,
    refetch: refetchSegments,
  } = useSalesSegmentAsSelect();

  useEffect(() => {
    refetchAccessTypes();
    setRole(undefined);
    clearErrors();
  }, [type]);

  useEffect(() => {
    setSegmentList(segments || []);
  }, [segments]);

  const handleSelectedGroupsChange = async (items?: SelectItem[]) => {
    setFetching(true);
    setSelectedGroups(items || []);

    if (items?.length) {
      let headOfficeResponses: any[] = [];
      let branchesResponses: any[] = [];

      items.forEach(async item => {
        getCompaniesByGroupAsSelect(Number(item.value), 9999).then(response => {
          headOfficeResponses = [
            ...headOfficeResponses,
            ...response.filter(company => company.isHeadOffice),
          ];
          branchesResponses = [
            ...branchesResponses,
            ...response.filter(company => !company.isHeadOffice),
          ];
          setHeadOffices(headOfficeResponses);
          setBranches(branchesResponses);
          setFetching(false);
        });
      });
    } else {
      setSelectedHeadOffices([]);
      setHeadOffices([]);
      setSelectedBranches([]);
      setBranches([]);
      setFetching(false);
    }
  };

  const ClientRoleId =
    externalRoles?.results[
      externalRoles?.results.findIndex(
        it => it.nm_funcao.toLowerCase() === 'cliente',
      )
    ].id_funcao || 0;

  const ClientMasterRoleId =
    externalRoles?.results[
      externalRoles?.results.findIndex(
        it => it.nm_funcao.toLowerCase() === 'cliente master',
      )
    ].id_funcao || 0;

  const handleSelectedHeadOfficesChange = (items?: SelectItem[]) => {
    setFetching(true);
    setSelectedHeadOffices(items || []);
    let responses: any[] = [];
    const groupIds = selectedGroups.map(item => item.value);

    if (groupIds && items?.length) {
      groupIds.forEach(async item => {
        getCompaniesByGroupAsSelect(Number(item), 9999).then(response => {
          responses = [
            ...responses,
            ...response.filter(company => !company.isHeadOffice),
          ];

          setBranches(responses);
          setFetching(false);
        });
      });
    } else {
      setSelectedBranches([]);
      setBranches([]);
      setFetching(false);
    }
  };

  const handleSelectedBranchesChange = (items?: SelectItem[]) => {
    setSelectedBranches(items || []);
  };

  useEffect(() => {
    setNoAccess(false);
    if (isEdition && userId) {
      const path = type === 'Cliente' ? 'user_customer' : 'user_corporate';

      setFetching(true);
      api
        .get(`/${path}/${userId}`)
        .then(response => {
          setUser(response.data);
          setStatus(response.data.status);
          setBloqueado(response.data.bloqueado);
          setAccessType(response.data.accessType);
          setRole(response.data.id_funcao);
          setResponsavel(response.data.responsavel);
          setFetching(false);

          loadCompanies();
        })
        .catch(erro => {
          setFetching(false);
          toast({
            title: 'Erro ao buscar usuário',
            description: erro.response.data.message || 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
          setNoAccess(true);
        });
    }
    getGroupsAsSelect(9999).then(response => setGroups(response));
  }, [isEdition, type, userId]);

  const loadCompanies = () => {
    getCompaniesByUser(Number(userId), type).then(response => {
      const userGroups = response.groups.map(group => ({
        label: group.nm_grupoeconomico,
        value: String(group.id_grupoeconomico),
      }));

      const userCompanies = response.companies.map(company => ({
        value: String(company.id_empresa),
        label: `${company.cnpjFormatado} - ${company.nm_fantasia}`,
        isHeadOffice: company.nu_cnpjordemraiz === '0001',
      }));

      handleSelectedGroupsChange(userGroups);
      handleSelectedHeadOfficesChange(
        userCompanies.filter(company => company.isHeadOffice),
      );

      setBranches(userCompanies.filter(company => !company.isHeadOffice));
      handleSelectedBranchesChange(
        userCompanies.filter(company => !company.isHeadOffice),
      );
    });
  };

  useEffect(() => {
    if (type === 'Interno') {
      internalRefetchRole();
    } else {
      externalRefetchRole();
    }
  }, [type]);

  useEffect(() => {
    setValue('name', user?.name);
    setValue('email', user?.email);
    setValue('id_funcao', user?.id_funcao);
    setValue('status', user?.status);
    setValue('blocked', user?.bloqueado);
    user?.status && setStatus(user.status);
    user?.responsavel && setResponsavel(user.responsavel);
    user?.bloqueado && setBloqueado(user.bloqueado);
    setValue('observacao', user?.observation);
    setValue('accessType', user?.accessType);
    setValue('codigo_sap', user?.codigo_sap || '');
    setTelefone(user?.telefone_comercial || '');
    setComissionado(user?.comissionado || false);
    setVisualizarComissoes(user?.visualizar_comissao || false);
    setClienteSucesso(user?.customer_success || false);
    const seguimentosSelecionados = user?.segmento_vendas?.map(segment => ({
      value: segment.id.toString(),
      label: segment.nome,
    }));
    handleSelectedSegments(seguimentosSelecionados);
  }, [user]);

  useEffect(() => {
    const updatedSegments = selectedSegments.map(obj1 => {
      const obj2 = segments?.find(
        item => item.value.toString() === obj1.value.toString(),
      );
      if (obj2) {
        return { value: obj1.value, label: obj2.label };
      }
      return obj1;
    });
    handleSelectedSegments(updatedSegments);
  }, [segments]);

  const validate = (formData: FormData) => {
    const { name, email, codigo_sap } = formData;

    !name?.trim() &&
      setError('name', { type: 'required', message: 'Nome é obrigatório' });
    name?.trim().length > 120 &&
      setError('name', {
        type: 'maxLength',
        message: 'Máximo de 120 caracteres',
      });
    !email?.trim() &&
      setError('email', { type: 'required', message: 'E-mail é obrigatório' });
    if (email?.trim() && !validateEmail(email?.trim())) {
      setError(`email`, {
        type: 'pattern',
        message: 'E-mail inválido',
      });
    }
    if (email?.trim() && email?.trim().length > 120) {
      setError(`email`, {
        type: 'maxLength',
        message: 'Máximo de 120 caracteres',
      });
    }
    ((isGerente() && comissionado) || isParceiroComercial()) &&
      !codigo_sap?.trim() &&
      setError('codigo_sap', {
        type: 'required',
        message: 'Código SAP é obrigatório',
      });
    (isGerente() || isParceiroComercial()) &&
      codigo_sap?.trim().length > 30 &&
      setError('codigo_sap', {
        type: 'required',
        message: 'Máximo de 30 caracteres',
      });
    ((isGerente() && comissionado) || isParceiroComercial()) &&
      !telefone &&
      setError('telefone_comercial', {
        type: 'required',
        message: 'Telefone comercial é obrigatório',
      });

    !accessType &&
      setError('accessType', {
        type: 'required',
        message: 'Tipo de acesso é obrigatório',
      });

    !id_funcao &&
      setError('id_funcao', {
        type: 'required',
        message: 'Tipo de função é obrigatório',
      });
    // eslint-disable-next-line no-underscore-dangle
    const { errors: errors2 } = control._formState;
    return !Object.keys(errors2).length;
  };
  async function handleCreate(formData: FormData) {
    clearErrors();
    const path = type === 'Cliente' ? 'user_customer' : 'user_corporate';

    const { name, email, codigo_sap } = formData;

    if (!validate(formData)) return;

    const form =
      id_funcao === ClientRoleId
        ? {
            name,
            email,
            status,
            accessType,
            id_funcao,
            bloqueado,
            multifator: user?.multifator,
            responsavel,
          }
        : {
            name,
            email,
            status,
            accessType,
            id_funcao,
            bloqueado,
            multifator: user?.multifator,
            ...(isGerente() && {
              codigo_sap,
              telefone_comercial: telefone,
              visualizar_comissao: visualizarComissoes,
              comissionado,
            }),
            ...(isParceiroComercial() && {
              codigo_sap,
              telefone_comercial: telefone,
            }),
            ...(isOperacionalOuSuporteOuAdm() && {
              customer_success: clienteSucesso,
            }),
          };
    setFetching(true);
    try {
      const companyIds: string[] = [];
      if (accessType === 'GROUP') {
        const headOfficeIds = headOffices.map(item => item.value);
        const branchIds = branches.map(item => item.value);
        companyIds.push(...[...branchIds, ...headOfficeIds]);
      } else {
        const headOfficeIds = selectedHeadOffices.map(item => item.value);
        const branchIds = selectedBranches.map(item => item.value);
        companyIds.push(...[...branchIds, ...headOfficeIds]);
      }

      const segmentsIds = selectedSegments.map(item => item.value);

      if (isEdition) {
        const { data: updatedUser } = await api.put(`${path}/${userId}`, form);
        await api.put(`${path}/${userId}/empresas`, companyIds);
        await api.post(`${path}/${userId}/segmento_vendas`, segmentsIds);

        isYourself && updateUser(updatedUser as AuthUser);
      } else {
        const { data } = await api.post<{
          id_usuarioexterno: number;
          id_usuariointerno: number;
        }>(`${path}`, form);

        const { id_usuarioexterno, id_usuariointerno } = data;

        await api.put(
          `${path}/${id_usuarioexterno || id_usuariointerno}/empresas`,
          companyIds,
        );
        await api.post(
          `${path}/${id_usuarioexterno || id_usuariointerno}/segmento_vendas`,
          segmentsIds,
        );
      }
      setFetching(false);
      toast({
        title: `Usuário ${isEdition ? 'alterado' : 'criado'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries();
      history.goBack();
    } catch (e: any) {
      setFetching(false);
      if (e.response.status === 400) {
        toast({
          title: `Erro ao ${isEdition ? 'alterar' : 'criar'} usuário`,
          description: e.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title: `Erro ao ${isEdition ? 'alterar' : 'criar'} usuário`,
          description: 'Tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  }

  const handleSelectedSegments = (items?: SelectItem[]) => {
    setSelectedSegments(items || []);
    if (items?.length) {
      const updatedSegmentsList = segments?.filter(
        item =>
          !items.some(obj => obj.value.toString() === item.value.toString()),
      );
      setSegmentList(updatedSegmentsList || []);
    } else {
      setSegmentList(segments || []);
    }
  };

  function handleTypeChange(e: string) {
    reset();
    setType(e);
    setStatus(false);
  }

  function handleAccessType(e: any) {
    setAccessType(e.target.value);
    if (e.target.options.selectedIndex !== 0) {
      clearErrors('accessType');
    }
  }

  function handleRole(e: any) {
    setRole(parseInt(e.target.value, 10));
    if (e.target.options.selectedIndex !== 0) {
      clearErrors('id_funcao');
    }
  }

  const opcoesComissao = () => {
    if (isGerente()) {
      return (
        <Stack gridGap={1}>
          <Checkbox
            colorScheme="b"
            isChecked={comissionado}
            onChange={() => {
              if (comissionado) {
                clearErrors('codigo_sap');
                clearErrors('telefone_comercial');
              }
              setComissionado(!comissionado);
            }}
            fontSize="sm"
          >
            Marque essa opção se o <b>Gerente de Contas</b> for comissionado.
          </Checkbox>
          <Checkbox
            colorScheme="b"
            isChecked={visualizarComissoes}
            onChange={() => {
              setVisualizarComissoes(!visualizarComissoes);
            }}
            fontSize="sm"
          >
            Permitir <b>Gerente de Contas</b> visualizar comissões dos{' '}
            <b>Parceiros Comerciais</b>.
          </Checkbox>
        </Stack>
      );
    }
    if (isOperacionalOuSuporteOuAdm()) {
      return (
        <Stack gridGap={1}>
          <Checkbox
            colorScheme="b"
            isChecked={clienteSucesso}
            onChange={() => {
              setClienteSucesso(!clienteSucesso);
            }}
            fontSize="sm"
          >
            Esse usuário tem perfil de Customer Success.
          </Checkbox>
        </Stack>
      );
    }
    return null;
  };

  const isGerente = () => {
    return id_funcao === RoleEnum.GERENTE_CONTAS;
  };

  const isParceiroComercial = () => {
    return id_funcao === RoleEnum.PARCEIRO_COMERCIAL;
  };

  const isOperacionalOuSuporteOuAdm = () => {
    return (
      id_funcao === RoleEnum.SUPORTE ||
      id_funcao === RoleEnum.OPERACIONAL ||
      id_funcao === RoleEnum.ADMIN
    );
  };

  // Criar componente para tipo telefone
  const ajusta = (v: string) => {
    const digitos = !v ? '' : v.replace(/[^\d]/g, '');
    if (!digitos || digitos.length < 10) return v;
    const corte = digitos.length === 10 ? 6 : 7;
    const max = digitos.length > 11 ? 11 : digitos.length;
    return `(${digitos.substring(0, 2)}) ${digitos.substring(
      2,
      corte,
    )}-${digitos.substring(corte, max)}`;
  };
  const maskBuilder = (t: any) => {
    if (!t || t.length === 0) return '';
    const a = ajusta(t);
    return a.length >= 6 && a[5] === '9' ? '(99) 99999-9999' : '(99) 9999-9999';
  };

  const handleChangeTelefone = (e: any) => {
    clearErrors('telefone_comercial');
    setTelefone(ajusta(e.target.value));
  };

  const dadosAdicionaisLogin = () => {
    if (isGerente() || isParceiroComercial()) {
      return (
        <HStack gridGap={4} alignItems="baseline">
          <Input
            name="telefone_comercial"
            type="text"
            label={
              isGerente() && !comissionado
                ? 'Telefone comercial'
                : 'Telefone comercial *'
            }
            error={errors.telefone_comercial}
            fontSize="15px"
            mask={maskBuilder(telefone)}
            isInputMask
            value={telefone}
            onChange={handleChangeTelefone}
          />
          <Input
            name="codigo_sap"
            type="text"
            label={isGerente() && !comissionado ? 'Código SAP' : 'Código SAP *'}
            register={register}
            error={errors.codigo_sap}
            fontSize="15px"
          />
        </HStack>
      );
    }
    return null;
  };

  return (
    <Stack as="form" onSubmit={handleSubmit(handleCreate)}>
      <Flex flexDir="column" justify="space-between" height="fit-content">
        <Flex flexDir="column" px="8" py="4" minH="calc(100vh - 127px)">
          <Heading hasBackButton>
            {isEdition ? 'Editar usuário' : 'Criar usuário'}
            {isFetching && (
              <Spinner style={{ marginLeft: '20px', height: 20 }} />
            )}
          </Heading>
          <Flex
            flexDir={{ md: 'row', sm: 'column' }}
            w="100%"
            py={9}
            gridGap="12"
          >
            <Stack gridGap={6} bg="white" w="40wh">
              <Stack gridGap={4}>
                <Heading fontSize="md">Tipo de Usuário</Heading>
                <RadioGroup
                  colorScheme="b"
                  value={type || undefined}
                  onChange={e => handleTypeChange(e)}
                  isDisabled={isEdition || userIsMasterClient}
                >
                  <Stack>
                    <Radio value="Interno" {...register('type')}>
                      Usuário interno
                    </Radio>
                    <Radio value="Cliente" {...register('type')}>
                      Usuário externo
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Stack>
              <Stack gridGap={4} bg="white">
                <Heading fontSize="md">Funções e permissões</Heading>
                <HStack gridGap={4} alignItems="baseline">
                  <Select
                    name="id_funcao"
                    placeholder="Selecione"
                    label="Tipo de função *"
                    error={errors.id_funcao}
                    onChange={e => handleRole(e)}
                    fontSize="15px"
                    value={id_funcao}
                  >
                    {(type === 'Interno' &&
                      !internalRoleLoading &&
                      internalRoles?.results.map(rol => (
                        <option key={rol.id_funcao} value={rol.id_funcao}>
                          {rol.nm_funcao}
                        </option>
                      ))) ||
                      (type === 'Cliente' &&
                        !externalRoleLoading &&
                        userIsMasterClient &&
                        externalRoles?.results
                          .filter(
                            item =>
                              item.nm_funcao.toLowerCase() !==
                              RolesEnum.PARCEIRO_COMERCIAL,
                          )
                          .map(rol => (
                            <option key={rol.id_funcao} value={rol.id_funcao}>
                              {rol.nm_funcao}
                            </option>
                          ))) ||
                      (type === 'Cliente' &&
                        !externalRoleLoading &&
                        !userIsMasterClient &&
                        externalRoles?.results.map(rol => (
                          <option key={rol.id_funcao} value={rol.id_funcao}>
                            {rol.nm_funcao}
                          </option>
                        )))}
                  </Select>
                  <Select
                    name="accessType"
                    placeholder="Selecione"
                    label="Tipo de acesso *"
                    value={accessType}
                    onChange={e => handleAccessType(e)}
                    error={errors.accessType}
                    fontSize="15px"
                  >
                    {!accessTypesLoading &&
                      accessTypes?.map(item => (
                        <option key={item.accessType} value={item.accessType}>
                          {item.formattedAccessType}
                        </option>
                      ))}
                  </Select>
                </HStack>
                {opcoesComissao()}
              </Stack>
              <Stack gridGap={4}>
                <Heading fontSize="md">Dados de Login</Heading>
                <HStack gridGap={4} alignItems="baseline">
                  <Input
                    name="name"
                    type="text"
                    label="Nome *"
                    register={register}
                    error={errors.name}
                    fontSize="15px"
                  />
                  <Input
                    name="email"
                    type="email"
                    label="E-mail *"
                    register={register}
                    error={errors.email}
                    fontSize="15px"
                  />
                </HStack>
                {dadosAdicionaisLogin()}
                {/* {type !== 'Cliente' && ( */}
                {/*  <Input */}
                {/*    name="login" */}
                {/*    type="text" */}
                {/*    label="Login na rede" */}
                {/*    register={register} */}
                {/*    error={errors.login} */}
                {/*    fontSize="15px" */}
                {/*  /> */}
                {/* )} */}
              </Stack>
              <Stack gridGap={4}>
                <Heading fontSize="md">Situação</Heading>
                <FormControl
                  isDisabled={isYourself || (type === 'Cliente' && !isEdition)}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <FormLabel htmlFor="status" mb="0">
                    Ativo
                  </FormLabel>
                  <Switch
                    isDisabled={
                      isYourself || (type === 'Cliente' && !isEdition)
                    }
                    id="status"
                    size="lg"
                    colorScheme="b"
                    {...register('status')}
                    isChecked={status}
                    onChange={e => setStatus(e.target.checked)}
                  />
                </FormControl>
                {type === 'Cliente' && (
                  <FormControl
                    isDisabled
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <FormLabel htmlFor="blocked" mb="0">
                      Desbloqueado
                    </FormLabel>
                    <Switch
                      isDisabled
                      id="bloqueado"
                      size="lg"
                      colorScheme="b"
                      {...register('blocked')}
                      isChecked={!bloqueado}
                      onChange={e => setBloqueado(e.target.checked)}
                    />
                  </FormControl>
                )}
                {type === 'Cliente' &&
                  (id_funcao === ClientRoleId ||
                    id_funcao === ClientMasterRoleId) && (
                    <FormControl
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <FormLabel htmlFor="responsavel" mb="0">
                        Responsável por contratos
                      </FormLabel>
                      <Switch
                        id="responsavel"
                        size="lg"
                        colorScheme="b"
                        {...register('responsavel')}
                        isChecked={responsavel}
                        onChange={e => {
                          setResponsavel(e.target.checked);
                        }}
                      />
                    </FormControl>
                  )}
              </Stack>
            </Stack>
            <Stack gridGap={6} bg="white" w={['100%', '100%', '50%', '40%']}>
              {(isGerente() || isParceiroComercial()) && (
                <Stack gridGap={4}>
                  <Heading fontSize="md">Segmento de vendas</Heading>
                  {/* <SelectableItemWithActions */}
                  {/*  carregando={false} */}
                  {/*  lista={[ */}
                  {/*    { id: 1, name: 'Teste 1' }, */}
                  {/*    { id: 2, name: 'Teste 2' }, */}
                  {/*  ]} */}
                  {/*  isMulti */}
                  {/* /> */}
                  <InputGroup>
                    <ReactSelect
                      isMulti
                      isSearchable
                      label="Segmento de vendas"
                      placeholder="Selecione"
                      options={segmentList}
                      value={selectedSegments}
                      onChange={changes =>
                        handleSelectedSegments(changes as SelectItem[])
                      }
                    />
                    <SalesSegmentForm externalUser={false} />
                  </InputGroup>
                </Stack>
              )}
              {accessType !== 'ADMIN' && (
                <Stack gridGap={4} w="calc(100% - 80px)">
                  <Heading fontSize="md">Vínculo</Heading>
                  {!!groups.length && (
                    // <AutoComplete
                    //   label="Grupos"
                    //   placeholder="Selecione"
                    //   items={groups}
                    //   selectedItems={selectedGroups}
                    //   onSelectedItemsChange={changes =>
                    //     handleSelectedGroupsChange(changes.selectedItems)
                    //   }
                    // />
                    <ReactSelect
                      isMulti
                      isSearchable
                      label="Grupos"
                      placeholder="Selecione"
                      options={groups}
                      value={selectedGroups}
                      onChange={changes =>
                        handleSelectedGroupsChange(changes as SelectItem[])
                      }
                    />
                  )}
                  {!!headOffices.length && (
                    // <AutoComplete
                    //   label="Matrizes"
                    //   placeholder="Selecione"
                    //   items={headOffices}
                    //   onCreateItem={e => console.log(e)}
                    //   listStyleProps={{
                    //     padding: '0',
                    //   }}
                    //   selectedItems={selectedHeadOffices}
                    //   onSelectedItemsChange={changes =>
                    //     handleSelectedHeadOfficesChange(changes.selectedItems)
                    //   }
                    // />
                    <ReactSelect
                      isMulti
                      isSearchable
                      label="Matrizes"
                      placeholder="Selecione"
                      options={headOffices}
                      value={selectedHeadOffices}
                      onChange={changes =>
                        handleSelectedHeadOfficesChange(changes as SelectItem[])
                      }
                    />
                  )}
                  {!!branches.length && (
                    // <AutoComplete
                    //   label="Filiais"
                    //   placeholder="Selecione"
                    //   items={branches}
                    //   onCreateItem={e => console.log(e)}
                    //   listStyleProps={{
                    //     padding: '0',
                    //   }}
                    //   selectedItems={selectedBranches}
                    //   onSelectedItemsChange={changes =>
                    //     handleSelectedBranchesChange(changes.selectedItems)
                    //   }
                    // />

                    <ReactSelect
                      isMulti
                      isSearchable
                      options={branches}
                      label="Filiais"
                      placeholder="Selecione"
                      value={selectedBranches}
                      onChange={changes =>
                        handleSelectedBranchesChange(changes as SelectItem[])
                      }
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Flex>
        </Flex>
      </Flex>
      <Footer>
        <Button
          variant="secondary"
          onClick={() => history.goBack()}
          isDisabled={isFetching}
        >
          Cancelar
        </Button>
        <Button type="submit" isDisabled={isFetching || noAccess}>
          {isEdition ? 'Alterar' : 'Cadastrar'}{' '}
          {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
        </Button>
      </Footer>
    </Stack>
  );
};
